// src/ClientAssessment.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Send, Cat, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';

const ClientAssessment = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [showTherapistList, setShowTherapistList] = useState(false);
  const [therapistList, setTherapistList] = useState([]);
  
  // New states for dynamic questions
  const [questions, setQuestions] = useState([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  
  // Now this is correctly inside a component
  const navigate = useNavigate();
  
  // Create a ref for the textarea to maintain focus
  const textareaRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);
  
  // Effect to clean up speech recognition
  useEffect(() => {
    // If we have an active recognition instance, stop it
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    
    // Clean up function to ensure speech recognition is stopped
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [currentStep]);

  // Function to initialize and start speech recognition
  const toggleVoiceInput = (e) => {
    // Prevent default focus behavior
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Voice button clicked");
    
    // If already listening, stop it
    if (isListening && recognitionRef.current) {
      console.log("Stopping speech recognition");
      
      // IMPORTANT: Store the current text before stopping
      // This prevents the text from disappearing when stopping
      const currentText = textareaRef.current?.value || '';
      
      // First stop the recognition
      recognitionRef.current.stop();
      setIsListening(false);
      
      // Then ensure the text is preserved in the textarea
      // Use a small timeout to let the recognition fully stop
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.value = currentText;
        }
      }, 10);
      
      return;
    }
    
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert("Your browser doesn't support speech recognition. Please try a different browser.");
      return;
    }
    
    try {
      // Get current text before creating recognition instance
      const existingText = textareaRef.current?.value || '';
      
      // Create a new recognition instance each time
      recognitionRef.current = new SpeechRecognition();
      const recognition = recognitionRef.current;
      
      // Configure recognition
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US'; // Set language explicitly
      
      // Track the accumulated transcript in a variable that persists between events
      let finalTranscript = existingText;
      
      // Set up event handlers
      recognition.onstart = () => {
        console.log("Speech recognition started");
        // IMPORTANT: Do NOT clear the textarea here
        setIsListening(true);
      };
      
      recognition.onresult = (event) => {
        console.log("Speech recognition result received");
        
        // Get the most recent result
        let interimTranscript = '';
        
        // Process only new results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            // For final results, append to final transcript
            finalTranscript += ' ' + transcript;
          } else {
            // For interim results, add to interim transcript
            interimTranscript += transcript;
          }
        }
        
        // Combine final transcript (saved results) with interim (in-progress results)
        const currentTranscript = (finalTranscript + ' ' + interimTranscript).trim();
        
        // Update current response
        if (textareaRef.current) {
          textareaRef.current.value = currentTranscript;
        }
      };
      
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          alert("Microphone permission was denied. Please allow microphone access in your browser settings.");
        } else {
          alert(`Speech recognition error: ${event.error}`);
        }
      };
      
      recognition.onend = () => {
        console.log("Speech recognition ended");
        
        // IMPORTANT: When speech ends by itself (not from button click)
        // we need to preserve the current text
        const currentText = textareaRef.current?.value || '';
        setIsListening(false);
        
        // Make sure the text stays in the textarea after speech ends
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.value = currentText;
          }
        }, 10);
      };
      
      // This will trigger the permission dialog
      console.log("Starting speech recognition");
      recognition.start();
      
    } catch (error) {
      console.error("Error initializing speech recognition:", error);
      setIsListening(false);
      alert(`Failed to start speech recognition: ${error.message}`);
    }
  };

  // Fetch questions when component mounts
  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Effect to focus textarea and reset value when changing steps
  useEffect(() => {
    // After currentStep changes, focus the textarea if available
    if (textareaRef.current && currentStep < questions.length) {
      // Reset the textarea value to match the current response or empty string
      const currentQuestionId = questions[currentStep].id;
      textareaRef.current.value = responses[currentQuestionId] || '';
      
      // Small delay to ensure DOM has updated then focus
      setTimeout(() => {
        textareaRef.current.focus();
      }, 50);
    }
  }, [currentStep, questions, responses]);

  // Function to fetch assessment questions from the server
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/get-assessment-questions');
      
      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }
      
      const data = await response.json();
      
      if (data.success && data.questions && data.questions.length > 0) {
        console.log('Loaded questions from therapist contributions:', data.questions);
        setQuestions(data.questions);
      } else {
        // No questions available - show empty state
        console.log('No questions available from therapist contributions');
        setQuestions([]);
      }
      
      setQuestionsLoaded(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Set empty array on error
      setQuestions([]);
      setQuestionsLoaded(true);
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Get the current question ID
    const currentQuestionId = questions[currentStep].id;
    
    // Get the current response directly from the textarea
    const currentValue = textareaRef.current ? textareaRef.current.value.trim() : '';
    
    // Stop recording if it's active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  
    // Don't proceed if the field is empty
    if (!currentValue) {
      alert('Please provide an answer before continuing');
      return;
    }
    
    // Now update the responses state with the current value
    const updatedResponses = {
      ...responses,
      [currentQuestionId]: currentValue
    };
    setResponses(updatedResponses);
    
    // Log updated responses for debugging
    console.log("Updated responses:", updatedResponses);
    console.log("Current question:", questions[currentStep]);
      
    // Process the response
    setLoading(true);
    try {
      // Call API to process individual response
      // Get authentication data from localStorage
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const token = localStorage.getItem('authToken');
      
      // Prepare headers with auth token if available
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log("Using authorization header with token");
      }
      
      // Include both the client-friendly ID and original MongoDB ID if available
      const currentQuestion = questions[currentStep];
      const requestBody = {
        questionId: currentQuestionId,
        response: currentValue,
        userId: user?.id // Include userId as fallback
      };
      
      // If original ID exists, include it to help with database lookups
      if (currentQuestion.originalId) {
        requestBody.originalQuestionId = currentQuestion.originalId;
      }
      
      fetch('/api/process-response', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        credentials: 'include' // Include cookies in the request
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to process response');
        }
        
        if (currentStep < questions.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          // Final submission - analyze all responses
          return submitAllResponses(updatedResponses);
        }
      })
      .then(result => {
        if (result) {
          console.log("Analysis result:", result);
          setRecommendation(result);
          setComplete(true);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error processing response:', error);
        alert('There was an error processing your response. Please try again.');
        setLoading(false);
      });
    } catch (error) {
      console.error('Error setting up request:', error);
      alert('There was an error processing your response. Please try again.');
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      if (isListening && recognitionRef.current) {
        recognitionRef.current.stop();
        setIsListening(false);
      }

      // Save current response before navigation
      if (textareaRef.current) {
        const currentValue = textareaRef.current.value;
        const currentQuestionId = questions[currentStep].id;
        setResponses(prev => ({
          ...prev,
          [currentQuestionId]: currentValue
        }));
      }
      
      // Navigate to previous step
      setCurrentStep(currentStep - 1);
    }
  };

  // Function to submit all responses for final analysis
  const submitAllResponses = async (allResponses) => {
    try {
      // Log the responses for debugging
      console.log("Submitting responses for analysis:", allResponses);
      
      // For the analyze-responses endpoint, we need to ensure we're using
      // the appropriate keys expected by the server-side code
      const formattedResponses = {};
      
      // Reformat responses to use question-specific expected keys
      // This mapping is based on the server's expected format
      Object.keys(allResponses).forEach(questionId => {
        // For the core relationship questions, map to the expected keys
        // This is based on the server's analyze-responses endpoint expectations
        if (questionId.includes('partner_relationship') || questions.find(q => q.id === questionId && q.text.toLowerCase().includes('partner'))) {
          formattedResponses.partner_relationship = allResponses[questionId];
        } 
        else if (questionId.includes('friend_relationship') || questions.find(q => q.id === questionId && q.text.toLowerCase().includes('friend'))) {
          formattedResponses.friend_relationship = allResponses[questionId];
        }
        else if (questionId.includes('teacher_relationship') || questions.find(q => q.id === questionId && q.text.toLowerCase().includes('teacher'))) {
          formattedResponses.teacher_relationship = allResponses[questionId];
        }
        // Keep the original mapping as well
        else {
          formattedResponses[questionId] = allResponses[questionId];
        }
      });
      
      console.log("Formatted responses:", formattedResponses);
      
      // Get authentication data from localStorage
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      const token = localStorage.getItem('authToken');
      
      // Prepare headers with auth token if available
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log("Using authorization header with token");
      }
      
      const response = await fetch('/api/analyze-responses', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          responses: formattedResponses,
          userId: user?.id // Include userId explicitly in the request body
        }),
        credentials: 'include' // Include cookies in the request
      });
      
      if (!response.ok) {
        throw new Error('Failed to analyze responses');
      }
      
      const data = await response.json();
      return data.recommendation;
    } catch (error) {
      console.error('Error analyzing responses:', error);
      throw error;
    }
  };

  // Function to fetch all therapists
  const fetchTherapists = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/therapists');
      
      if (!response.ok) {
        throw new Error('Failed to fetch therapists');
      }
      
      const data = await response.json();
      setTherapistList(data.therapists);
      setShowTherapistList(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      setLoading(false);
      alert('Failed to load therapist list');
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-blue-600 text-white p-4 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center">
            <Cat className="mr-2" size={28} />
            <h1 className="text-xl md:text-2xl font-bold">Mindaera</h1>
            <span className="ml-3 bg-blue-800 text-xs px-2 py-1 rounded-full">Assessment</span>
            </div>
            <div className="flex items-center space-x-4">
            <Link 
                to="/client-profile" 
                className="text-white hover:text-blue-200 text-sm font-medium"
            >
                My Profile
            </Link>
            <Link 
                to="/" 
                className="text-white hover:text-blue-200 text-sm font-medium"
            >
                Home
            </Link>
            <Link 
                to="/login" 
                className="text-white hover:text-blue-200 text-sm font-medium"
            >
                Login
            </Link>
            </div>
        </div>
        </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto py-8 px-4">
        {!questionsLoaded ? (
          // Loading state while fetching questions
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6 flex justify-center">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-gray-600">Loading assessment questions...</p>
            </div>
          </div>
        ) : questions.length === 0 ? (
          // No questions available state
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="rounded-full bg-yellow-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <span className="text-yellow-600 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Assessment Questions Available</h2>
            <p className="text-gray-600 mb-4">
              There are currently no assessment questions from our therapists. Please check back later.
            </p>
          </div>
        ) : !complete ? (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="h-2 bg-blue-100 rounded-full">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-300" 
                  style={{ width: `${(currentStep / (questions.length - 1)) * 100}%` }}
                ></div>
              </div>
              <div className="mt-2 text-sm text-blue-600 font-medium">
                Question {currentStep + 1} of {questions.length}
              </div>
            </div>

            {/* Question */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                {questions[currentStep].text}
              </h2>
              
              {/* Show source information for contributed questions */}
              {questions[currentStep].source && (
                <p className="text-xs text-gray-500 mb-2">
                  Contributed by Dr. {questions[currentStep].source.therapist.charAt(0).toUpperCase() + questions[currentStep].source.therapist.slice(1)}
                </p>
              )}
              
              <p className="text-sm text-blue-600 italic mb-4">
                Why we're asking: {questions[currentStep].reasoning}
              </p>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  id={`question-${questions[currentStep].id}`}
                  name={`question-${questions[currentStep].id}`}
                  className="w-full p-3 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-32"
                  placeholder="Type your answer here..."
                ></textarea>

                <button 
                  type="button"
                  onMouseDown={(e) => {
                    // Prevent focus from leaving textarea on mousedown
                    e.preventDefault();
                  }}
                  onClick={(e) => toggleVoiceInput(e)}
                  className={`absolute bottom-3 right-3 p-2 rounded-full ${
                    isListening 
                      ? 'bg-red-100 text-red-600 animate-pulse' 
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                  title={isListening ? "Stop recording" : "Answer with voice"}
                >
                  {isListening ? <Mic size={20} /> : <Mic size={20} />}
                </button>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0 || loading}
                className={`flex items-center px-4 py-2 rounded-md ${
                  currentStep === 0 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <ChevronLeft size={18} className="mr-1" />
                Previous
              </button>
              
              <button
                type="button" 
                onClick={handleNext}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  <>
                    {currentStep < questions.length - 1 ? (
                      <>
                        Next
                        <ChevronRight size={18} className="ml-1" />
                      </>
                    ) : (
                      <>
                        Submit
                        <Send size={18} className="ml-1" />
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          // Results screen - remains the same as before
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            {showTherapistList ? (
              // Therapist List View
              <div>
                <div className="mb-6">
                  <button 
                    onClick={() => setShowTherapistList(false)}
                    className="flex items-center text-blue-600 font-medium"
                  >
                    <ChevronLeft size={18} className="mr-1" />
                    Back to Recommendation
                  </button>
                  <h2 className="text-2xl font-bold text-gray-800 mt-4">All Available Therapists</h2>
                  <p className="text-gray-600">Explore our team of qualified mental health professionals.</p>
                </div>
                
                <div className="space-y-4">
                  {therapistList.map(therapist => (
                    <div key={therapist.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <h3 className="text-lg font-semibold text-gray-800">{therapist.name}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                        <div>
                          <p className="text-sm text-gray-500">Specialty</p>
                          <p>{therapist.specialty}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Experience</p>
                          <p>{therapist.experience}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-gray-500">Approach</p>
                        <p className="text-gray-700">{therapist.style}</p>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm text-gray-500">Key Characteristics</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {therapist.tags.map((tag, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      {recommendation && recommendation.id === therapist.id && (
                        <div className="mt-3 bg-green-100 text-green-800 px-3 py-2 rounded text-sm">
                          ★ Recommended Match for You
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
          // Completion screen - redirect to dashboard instead of showing therapist match
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Assessment Complete!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for completing your assessment. Your responses have been saved and your personality profile is being generated.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => navigate('/client-profile')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                >
                  Go to Your Dashboard
                </button>
                
                <button 
                  onClick={() => {
                    setCurrentStep(0);
                    setResponses({});
                    setComplete(false);
                    setRecommendation(null);
                    setShowTherapistList(false);
                  }}
                  className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                >
                  Take Assessment Again
                </button>
              </div>
              
              <div className="mt-8 text-sm text-gray-500">
                <p>You can find therapist matches by asking "How can we help you today?" in your dashboard.</p>
              </div>
            </div>
          </div>
        )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
        <div className="container mx-auto">
          Mindaera | Internal Dev Prototype | Not for public release
        </div>
      </footer>
    </div>
  );
};

export default ClientAssessment;